/* eslint-disable react/jsx-props-no-spreading */
import React from "react"
import { ThemeProvider } from "@mui/material"
import "./assets/scss/app.scss"
import "./assets/scss/mediaQueries.scss"
import theme from "./assets/theme/index"
import ImageService from "@front-apps/core/services/image"
import { imageMap } from "./utils/imageUtils"
import ErrorBoundary from "@front-apps/core/utils/ErrorBoundary"
import Api from "@front-apps/core/API/api"
import ComponentService from "@front-apps/core/services/component"
import AppGlobal from "@front-apps/core/utils/appGlobal"
import CwButton from "@front-apps/core/components/web/cw-button"
import CwPage from "@front-apps/core/components/web/cw-page"
import StoreService from "@front-apps/core/services/store"
import { PageConfigModel } from "@front-apps/core/models/pageConfig"
import NavigationService from "@front-apps/core/services/navigation"
import DialogService from "@front-apps/core/services/dialog"
import UserService from "@front-apps/core/services/user"
import CwSnackBar from "@front-apps/core/components/web/cw-snackbar"
import CwDialog from "@front-apps/core/components/web/cw-dialog"
import CwStepper from "@front-apps/core/components/web/stepper/cw-stepper"
import CwBox from "@front-apps/core/components/web/cw-box"
import ModuleService from "@front-apps/core/services/module"
import UIService from "@front-apps/core/services/UIService"
import CwText from "@front-apps/core/components/web/cw-text"
import CwSelect from "@front-apps/core/components/web/cw-select"
import CwEditor from "@front-apps/core/components/web/cw-editor/cw-editor"
import ComponentsUtils from "@front-apps/core/utils/components"
import GroupService from "@front-apps/core/services/group"
import SearchService from "@front-apps/core/services/search"
import SystemService from "@front-apps/core/services/system"
import WebNavigationParam from "@front-apps/core/services/webNavigationParam"
import ActivitiesService from "@front-apps/core/services/activities"
import CwAppBar from "@front-apps/core/components/web/cw-bar/cw-app-bar"
import GedService from "@front-apps/core/services/ged"
import i18n from "./assets/translations/i18n"
import CwProgress from "@front-apps/core/components/web/cw-progress"
import LocalPageConfigService from "./services/localPageConfig"
import CwTextfield from "@front-apps/core/components/web/cw-textfield"
import CwForm from "@front-apps/core/components/web/cw-form"
import CwQrCode from "@front-apps/core/components/web/cw-qrCode"
import DataService from "./services/data"
import LinkService from "./services/link"
import PartnerService from "./services/partners"
import CwRadioButtonList from "./components/cw-RadioButtonList"
import CwImage from "@front-apps/core/components/web/cw-image"
import CwMultiUnitInput from "./components/cw-multiUnitInput "
import CwGroupNestedList from "./components/cw-groupNestedList"
import CwBarcode from "./components/cw-barcode"
import CwMainCustomerFormWrapper from "./components/cw-mainCustomerFormWrapper"

@AppGlobal({
    components: [
        { key: "button", componentRef: CwButton },
        { key: "appBar", componentRef: CwAppBar },
        { key: "progress", componentRef: CwProgress },
        { key: "text", componentRef: CwText },
        { key: "stepper", componentRef: CwStepper },
        { key: "box", componentRef: CwBox },
        { key: "textField", componentRef: CwTextfield },
        { key: "select", componentRef: CwSelect },
        { key: "form", componentRef: CwForm },
        { key: "qrCode", componentRef: CwQrCode },
        { key: "radioButtonList", componentRef: CwRadioButtonList },
        { key: "image", componentRef: CwImage },
        { key: "multiUnitInput", componentRef: CwMultiUnitInput },
        { key: "groupNestedList", componentRef: CwGroupNestedList },
        { key: "editor", componentRef: CwEditor },
        { key: "barCode", componentRef: CwBarcode },
        {
            key: "mainCustomerFormWrapper",
            componentRef: CwMainCustomerFormWrapper,
        },
    ],
    providers: [
        { key: "ComponentService", serviceRef: ComponentService },
        { key: "StoreService", serviceRef: StoreService },
        { key: "DialogService", serviceRef: DialogService },
        { key: "UserService", serviceRef: UserService },
        { key: "NavigationService", serviceRef: NavigationService },
        { key: "ModuleService", serviceRef: ModuleService },
        { key: "UIService", serviceRef: UIService },
        { key: "GroupService", serviceRef: GroupService },
        { key: "SearchService", serviceRef: SearchService },
        { key: "SystemService", serviceRef: SystemService },
        { key: "ActivitiesService", serviceRef: ActivitiesService },
        { key: "GedService", serviceRef: GedService },
        { key: "LinkService", serviceRef: LinkService },
        { key: "NavigationParamService", serviceRef: WebNavigationParam },
        { key: "PageConfigService", serviceRef: LocalPageConfigService },
        { key: "DataService", serviceRef: DataService },
        { key: "PartnerService", serviceRef: PartnerService },
    ],
})
class App extends React.Component<unknown, { pageConfig?: PageConfigModel }> {
    private navigation

    constructor(props: unknown) {
        super(props)
        this.state = {}
        this.navigation = NavigationService.getInstance()
    }

    componentDidMount(): void {
        // chargement de toute les images dans le coeur de l'app
        ImageService.getInstance().setImageMap(imageMap)
        // init les listeners de changement de route
        this.navigation.init()
        // init gestion d'erreur globale des api
        Api.init()
        // start app
        this.init()
    }

    componentWillUnmount(): void {
        this.navigation.clearListeners()
    }

    async init(): Promise<void> {
        const webNavigationParam = WebNavigationParam.getInstance()

        // abonnement aux événements de routing
        this.navigation.listenRoutingEvents((pageConfig: PageConfigModel) => {
            // refresh si la page chargée est differente de celle courrante
            // eslint-disable-next-line react/destructuring-assignment
            if (pageConfig.name !== this.state.pageConfig?.name)
                this.setState({
                    pageConfig,
                })
        })

        const { params } = webNavigationParam.get()
        const groupId = params?.groupId
        const pdv = params?.pdv
        const partner = params?.partner
        try {
            StoreService.getInstance().local.set("groupId", groupId)
            const setting = await GroupService.getInstance().getSetting(
                "checkIn",
                groupId
            )
            const pdvSettings = pdv ? setting?.pdvs[pdv] : setting?.pdvs[0]
            if (pdvSettings) {
                StoreService.getInstance().local.set("setting", pdvSettings)
            } else {
                throw new Error("Setting not found")
            }
        } catch (error) {
            console.error("Error fetching checkin welcome settings:", error)
            UIService.getInstance().showErrorToast(
                i18n.t("failToGetTheGroup"),
                "App"
            )
            NavigationService.getInstance().navigateTo("error", {
                groupId,
            })
        }

        StoreService.getInstance().persistant.delete("lang")
        const navigatorLang = navigator.language.split("-")[0]
        if (params && params.lang) {
            i18n.changeLanguage(params.lang)
            StoreService.getInstance().persistant.set("lang", params.lang)
            await NavigationService.getInstance().navigateTo("welcome", {
                groupId: groupId,
                pdv: pdv,
                partner: partner,
            })
        } else {
            if (
                navigatorLang &&
                ["es", "en", "it", "fr", "de"].includes(navigatorLang)
            ) {
                i18n.changeLanguage(navigatorLang)
                StoreService.getInstance().persistant.set("lang", navigatorLang)
                await NavigationService.getInstance().navigateTo(
                    "welcome",
                    {
                        groupId: groupId,
                        pdv: pdv,
                        lang: navigatorLang,
                        partner: partner,
                    },
                    true
                )
            } else {
                await NavigationService.getInstance().navigateTo(
                    "languageChoice",
                    {
                        groupId: groupId,
                        pdv: pdv,
                        partner: partner,
                    },
                    true
                )
            }
        }
        try {
            StoreService.getInstance().local.set("groupId", groupId)
            const setting = await GroupService.getInstance().getSetting(
                "checkIn",
                groupId
            )

            const pdvSettings = pdv ? setting?.pdvs[pdv] : setting?.pdvs[0]
            if (pdvSettings) {
                StoreService.getInstance().local.set("setting", pdvSettings)
            } else {
                throw new Error("Setting not found")
            }
        } catch (error) {
            console.error("Error fetching checkin welcome settings:", error)
            UIService.getInstance().showErrorToast(
                i18n.t("failToGetTheGroup"),
                "App"
            )
            NavigationService.getInstance().navigateTo("error", {
                groupId,
            })
        }
    }

    renderElement(): React.JSX.Element {
        const { pageConfig } = this.state
        if (pageConfig) {
            return <CwPage page={pageConfig} />
        }
        return ComponentsUtils.renderLoader()
    }

    render(): React.JSX.Element {
        return (
            <ErrorBoundary>
                <ThemeProvider theme={theme}>
                    {/* Main Elements */}
                    {this.renderElement()}
                    {/* Elements fix de l'app */}
                    <CwSnackBar />
                    <CwDialog />
                </ThemeProvider>
            </ErrorBoundary>
        )
    }
}

export default App
